import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';
import { ensureSocket } from '../socket';
import SendHistory from '../History/SendHistory';
import ReceivedHistory from '../History/ReceivedHistory';
import { formatNumberPrecise } from '../Utils';

const StatCard = ({ icon, label, value, tone, sub }) => (
  <div className="ui-card p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-2xs font-semibold uppercase tracking-wider text-slate-500 truncate">{label}</p>
        <p className="text-2xl font-extrabold text-slate-100 mt-2 tabular-nums leading-none">{value}</p>
        {sub && <p className="text-2xs text-slate-600 mt-1.5">{sub}</p>}
      </div>
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>
        <i className={`fas ${icon} text-xs`} />
      </span>
    </div>
  </div>
);

const Dashboard = ({ userID, userType }) => {
  const [summary, setSummary] = useState({ transactionCount: 0, balance: 0, totalGold: 0, totalCommission: 0, totalSentAmount: 0, totalReceivedAmount: 0 });
  const [activeTab, setActiveTab] = useState('send');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const collectionName = userType === 'agency' ? 'agencies' : 'resellers';

  const load = useCallback(() => apiGet('/api/account/me')
    .then(({ profile: data }) => {
      if (data) {
        setSummary({ balance: data.Balance || 0, totalGold: data.totalGold || 0, totalCommission: data.totalCommission || 0, totalSentAmount: data.Send || 0, totalReceivedAmount: data.Received || 0, transactionCount: data.transactionCount || 0 });
        setError(null);
      } else { setError('User document not found'); }
      setLoading(false);
    })
    .catch(() => { setError('Failed to load dashboard data.'); setLoading(false); }), []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 60000); // safety net; live updates come via socket
    return () => clearInterval(poll);
  }, [load]);

  useEffect(() => {
    let sock;
    let alive = true;
    ensureSocket().then((s) => { if (!alive || !s) return; sock = s; s.on('account:updated', load); });
    return () => { alive = false; if (sock) sock.off('account:updated', load); };
  }, [load]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <span className="ui-spinner w-9 h-9" />
      <p className="text-sm text-slate-500">Loading dashboard…</p>
    </div>
  );

  if (error) return (
    <div className="ui-page">
      <div className="ui-error"><i className="fas fa-triangle-exclamation" />{error}</div>
      <button onClick={() => window.location.reload()} className="ui-btn-ghost">
        <i className="fas fa-rotate-right text-xs" /> Try again
      </button>
    </div>
  );

  const cards = [
    ...(userType === 'agency' ? [
      { icon: 'fa-coins', label: 'Total Gold Earned', value: formatNumberPrecise(summary.totalGold), tone: 'bg-amber-500/15 text-amber-400', sub: 'From all hosts this cycle' },
      { icon: 'fa-dollar-sign', label: 'Agency Commission', value: `$${summary.totalCommission.toFixed(2)}`, tone: 'bg-emerald-500/15 text-emerald-400', sub: 'Cumulative (USD)' },
    ] : []),
    { icon: 'fa-wallet', label: 'Current Balance', value: formatNumberPrecise(summary.balance), tone: 'bg-accent-soft text-accent', sub: 'Available to send' },
    { icon: 'fa-arrow-down-long', label: 'Total Received', value: formatNumberPrecise(summary.totalReceivedAmount), tone: 'bg-emerald-500/15 text-emerald-400' },
    { icon: 'fa-arrow-up-long', label: 'Total Sent', value: formatNumberPrecise(summary.totalSentAmount), tone: 'bg-orange-500/15 text-orange-400' },
    { icon: 'fa-right-left', label: 'Transactions', value: formatNumberPrecise(summary.transactionCount), tone: 'bg-violet-500/15 text-violet-400' },
  ];

  const tabs = [
    { key: 'send', label: 'Send History', icon: 'fa-arrow-up-long' },
    { key: 'received', label: 'Received History', icon: 'fa-arrow-down-long' },
  ];

  return (
    <div className="ui-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="ui-h1">Overview</h1>
          <p className="ui-sub">Real-time agency performance</p>
        </div>
        <span className="ui-badge-pos">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
        </span>
      </div>

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      <div className="ui-card overflow-hidden">
        <div className="flex border-b border-ink-500 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'text-accent border-accent'
                  : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}
            >
              <i className={`fas ${tab.icon} text-2xs`} />
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-3 sm:p-4">
          {activeTab === 'send'
            ? <SendHistory userID={userID} collectionName={collectionName} />
            : <ReceivedHistory userID={userID} collectionName={collectionName} />
          }
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
